import { List as ImmutableList } from 'immutable';
import { LruObjectCache, createCachedSelector } from 're-reselect';
import { createSelector } from 'reselect';

import { accountsUtils } from 'app-components/flux/accounts';
import { DateTime } from 'luxon';
import { getCurrentDateString } from '../date/dateSelectors';
import { ACCOUNTS_BALANCES_REDUCER_KEY } from './accountsBalancesReducer';
import { BalanceTotalsImmutable } from './accountsBalancesTypes';

export const getResourceStore = (state) => state[ACCOUNTS_BALANCES_REDUCER_KEY];

export const getLoadPending = (state) => getResourceStore(state).loadPending;
export const getIsLoading = (state) => getResourceStore(state).isLoading;
export const getError = (state) => getResourceStore(state).error;
export const getResourcesById = (state) => getResourceStore(state).resourcesById;

export const getLastSyncDate = (state) => getResourceStore(state).lastSyncDate;

// Filter by balanceType === 'CURRENT' if any 'CURRENT' balanceTypes exist (This is going to be the more accurate value)
const filterAndSortBalancesForAccount = (account, accountBalances) => {
  let alteredAccountBalances = accountBalances;

  alteredAccountBalances = alteredAccountBalances.filter((balance) => balance.accountId === account.id);

  const prioritizedBalanceType =
    accountsUtils.isInvestmentAccount(account) && !accountsUtils.isAccountManual(account) ? 'ONLINE' : 'CURRENT';

  // Reference MAP that contains all balances. If there are duplicate balances, it only contains the PRIORITIZED type for that date.
  const balanceTypesForDates = new Map();

  alteredAccountBalances.forEach((bal) => {
    if (!balanceTypesForDates.has(bal.balanceOn)) {
      // If the date is not included in the list, add it
      balanceTypesForDates.set(bal.balanceOn, bal.balanceType);
    } else if (balanceTypesForDates.get(bal.balanceOn) !== prioritizedBalanceType) {
      // If the date IS included in the list, and it is NOT the prioritized type, replace it
      balanceTypesForDates.set(bal.balanceOn, bal.balanceType);
    }
  });
  alteredAccountBalances = alteredAccountBalances.filter((bal) => {
    //  Include this balance if its balanceType equals the assigned balance type for the date in uniqueBalances
    return bal.balanceType === balanceTypesForDates.get(bal.balanceOn);
  });

  //  Sort the balances by date
  return alteredAccountBalances
    .valueSeq()
    .toArray()
    .sort((a, b) => {
      if (a.balanceOn > b.balanceOn) {
        return 1;
      } else if (b.balanceOn > a.balanceOn) {
        return -1;
      } else {
        return 0;
      }
    });
};

// This is _NOT_ going to always be accurate. But it is dirt cheap compared to parsing dates
// This should only be used as a performance short-circuit because it will never create false positives
const dirtyAddOneDay = (isoDate) => {
  const yearDashMonthDash = isoDate.slice(0, 8);
  const day = Number.parseInt(isoDate.slice(8, 10), 10);
  const newDay = day < 9 ? '0' + (day + 1) : day + 1;
  return `${yearDashMonthDash}${newDay}`;
};

// JS dates, not luxon
const jsDatesAreOnSameDay = (first, second) =>
  first.getDate() === second.getDate() &&
  first.getMonth() === second.getMonth() &&
  first.getFullYear() === second.getFullYear();

function jsAddDays(date, days) {
  date.setDate(date.getDate() + days);
}

function sameMonth(first, second) {
  return second.startsWith(first.slice(0, 7));
}

function fillDateRange(adjustedAccountBalances, startISO, endISO, amount) {
  // If we aren't crossing any month boundaries we can do some dirty string hacks to avoid creating/mutating Date objects
  if (sameMonth(startISO, endISO)) {
    let firstDate = startISO;
    const secondDate = endISO;
    // Since the month is the same we can cheat and use the dirty add day method
    firstDate = dirtyAddOneDay(firstDate);
    if (firstDate !== secondDate) {
      // We need to add the missing dates
      while (firstDate < secondDate) {
        adjustedAccountBalances.push({
          balanceAmount: amount,
          balanceOn: firstDate,
          fakeData: true,
        });
        firstDate = dirtyAddOneDay(firstDate);
      }
    }
  } else {
    const firstDate = new Date(startISO);
    const secondDate = new Date(endISO);
    jsAddDays(firstDate, 1);
    //  And there ARE missing dates in between
    if (!jsDatesAreOnSameDay(firstDate, secondDate)) {
      while (firstDate < secondDate) {
        adjustedAccountBalances.push({
          balanceAmount: amount,
          balanceOn: firstDate.toISOString().slice(0, 10),
          fakeData: true,
        });
        jsAddDays(firstDate, 1);
      }
    }
  }
}

const colorIndexMaxValue = 10;

//  Returns a list of account objects that include the account name, id, and list of balances connected to that account sorted by date
//  In addition, since QCS only updates changes to an account on specific dates, it fills in the blank dates with the
//  previous dates' value
export const getSimplifiedBalancesByAccount = createSelector(
  (state, accounts) => accounts,
  getResourcesById,
  getCurrentDateString,
  function balancesByAccountInternal(accounts, balances, todayISO) {
    const listOfAccountsByBalances = [];

    let index = 0;
    accounts?.forEach((account) => {
      if (!account) {
        return;
      }

      //  Only include the balances for the selected account
      const accountBalances = balances?.size ? filterAndSortBalancesForAccount(account, balances) : [];

      const adjustedAccountBalances = [];

      //  This handles filling in missing dates with the values of the previous date!
      for (let i = 0; i < accountBalances?.length; i++) {
        adjustedAccountBalances.push({
          balanceAmount: accountBalances[i].balanceAmount,
          balanceOn: accountBalances[i].balanceOn,
        });
        fillDateRange(
          adjustedAccountBalances,
          accountBalances[i].balanceOn,
          accountBalances[i + 1]?.balanceOn ?? todayISO,
          accountBalances[i].balanceAmount,
        );
      }
      // We need to make sure to add today on the end if it is not already there
      if (
        adjustedAccountBalances.length > 0 &&
        adjustedAccountBalances[adjustedAccountBalances.length - 1].balanceOn !== todayISO
      ) {
        adjustedAccountBalances.push({
          balanceAmount: accountBalances[accountBalances.length - 1].balanceAmount,
          balanceOn: todayISO,
        });
      }
      //  Push the balances to the account object!
      listOfAccountsByBalances.push({
        name: account.name,
        id: account.id,
        type: account.type,
        accountBalances: adjustedAccountBalances,
        colorIndex: index % colorIndexMaxValue,
      });
      index += 1;
    });

    //  Sort the list by longest held account.
    const sortedList = listOfAccountsByBalances.sort((a, b) => b.accountBalances.length - a.accountBalances.length);
    return ImmutableList(sortedList);
  },
);

export const formatBalancesForChart = createSelector(
  (state, props) => getSimplifiedBalancesByAccount(state, props.accounts),
  (state, props) => props.chartStartDate,
  (state, props) => props.chartEndDate,
  (accountData, chartStartDate, chartEndDate) => {
    const tempChartData = [];
    let start = DateTime.fromISO(chartStartDate);
    const end = DateTime.fromISO(chartEndDate).plus({ days: 1 });
    const range = []; //  The range of dates
    while (start < end) {
      range.push(start.toISODate());
      start = start.plus({ days: 1 });
    }
    //  let shouldShowGraph = false;

    //  For each day, go through each account and then add the day with account data to the tempChartData array
    range.forEach((myDate) => {
      const tempData = {
        date: myDate,
        containsBalances: false,
      };
      accountData?.forEach((account) => {
        account.accountBalances?.forEach((balance) => {
          if (balance.balanceOn === myDate) {
            //  shouldShowGraph = true;
            tempData.containsBalances = true;
            tempData[`${account.id}-balance`] = balance.balanceAmount;
          }
        });
      });
      tempChartData.push(tempData);
    });

    //  Only include days that contain balances! (This means days before any accounts existed will not be included)
    //  tempChartData = tempChartData.filter((day) => day.containsBalances);
    return tempChartData;
  },
);

export const getBalanceTotals = createCachedSelector(
  (state, props) => getSimplifiedBalancesByAccount(state, props.accounts),
  (state, props) => props.startDate,
  (state, props) => props.endDate,
  (myAcctBalances, startDate, endDate) => {
    let startBalance = 0;
    let endBalance = 0;
    let todayBalance = 0;
    let doesStartDayExist = false;
    let doesEndDayExist = false;
    myAcctBalances?.forEach((account) => {
      if (account.accountBalances.length)
        todayBalance += account.accountBalances[account.accountBalances.length - 1].balanceAmount;
      const specialStartDay = account.accountBalances.filter((day) => day.balanceOn === startDate);
      const specialEndDay = account.accountBalances.filter((day) => day.balanceOn === endDate);
      //  The following statement is so that if the first day of balances still fits within the daterange, the change in balance and percent still appear
      /*  if (!specialStartDay.length && account.accountBalances.length && startDate <= account.accountBalances[0].balanceOn && endDate > account.accountBalances[0].balanceOn) {
        specialStartDay.push(account.accountBalances[0]);
      } */

      if (specialStartDay.length) {
        doesStartDayExist = true;
        startBalance += specialStartDay[0].balanceAmount;
      }
      if (specialEndDay.length) {
        doesEndDayExist = true;
        endBalance += specialEndDay[0].balanceAmount;
      }
    });

    const balancesObject = BalanceTotalsImmutable({
      balanceToday: todayBalance,
      balanceChange: doesStartDayExist || doesEndDayExist ? endBalance - startBalance : null,
      percentChange:
        startBalance !== 0 && doesStartDayExist && doesEndDayExist
          ? ((endBalance - startBalance) / startBalance) * 100
          : null,
    });
    return balancesObject;
  },
)({
  keySelector: (state, props) => `${props.startDate}${props.endDate}` || 'all-start-end-balances',
  cacheObject: new LruObjectCache({ cacheSize: 3 }),
});

export const getBalancesForTable = createSelector(
  (state, props) => getSimplifiedBalancesByAccount(state, props.accounts),
  (state, props) => props.startDate,
  (state, props) => props.endDate,
  (myAcctBalances, startDate, endDate) => {
    const formattedBalances = myAcctBalances.map((account) => {
      if (account.accountBalances.length === 0) {
        return {
          colorIndex: account.colorIndex,
          accountName: account.name,
          oneDayBalance: null,
          oneDayPercent: null,
          specialDayBalance: null,
          specialDayPercent: null,
          todayBalance: null,
        };
      }
      const { colorIndex } = account;
      const accountName = account.name;
      const today = account.accountBalances[account.accountBalances.length - 1];
      const yesterday = account.accountBalances[account.accountBalances.length - 2];
      const oneDayBalance = today && yesterday ? today.balanceAmount - yesterday.balanceAmount : null;
      const oneDayPercent =
        today && yesterday
          ? ((today?.balanceAmount - yesterday?.balanceAmount) / yesterday?.balanceAmount) * 100
          : null;

      const specialStartDay = account.accountBalances.filter((day) => day.balanceOn === startDate);

      //  The following statement is so that if the first day of balances still fits within the daterange, the change in balance and percent still appear
      /*  if (!specialStartDay.length && startDate <= account.accountBalances[0].balanceOn && endDate > account.accountBalances[0].balanceOn) {
        specialStartDay.push(account.accountBalances[0]);
      } */
      const specialEndDay = account.accountBalances.filter((day) => day.balanceOn === endDate);

      //  The following statement is so that if the last day of balances still fits within the daterange, the change in balance and percent still appear
      /*  const lastBalanceIndex = account.accountBalances.length - 1;
      if (!specialEndDay.length && startDate < account.accountBalances[lastBalanceIndex].balanceOn && endDate >= account.accountBalances[lastBalanceIndex].balanceOn) {
        specialEndDay.push(account.accountBalances[lastBalanceIndex]);
      } */
      const specialDayBalance =
        specialStartDay.length && specialEndDay.length
          ? specialEndDay[0]?.balanceAmount - specialStartDay[0]?.balanceAmount
          : null;
      const specialDayPercent =
        specialStartDay.length && specialEndDay.length
          ? ((specialEndDay[0]?.balanceAmount - specialStartDay[0]?.balanceAmount) /
              specialStartDay[0]?.balanceAmount) *
            100
          : null;
      const todayBalance = today?.balanceAmount;

      return {
        colorIndex,
        accountName,
        oneDayBalance,
        oneDayPercent,
        specialDayBalance,
        specialDayPercent,
        todayBalance,
      };
    });

    return ImmutableList(formattedBalances);
  },
);

export const getSortedBalancesForTable = createSelector(
  (state, props) => getSimplifiedBalancesByAccount(state, props.accounts),
  (state, props) => props.startDate,
  (state, props) => props.endDate,
  (state, props) => props.orderBy,
  (state, props) => props.sortDirection,
  (myAcctBalances, startDate, endDate, orderBy, sortDirection) => {
    const formattedBalances = myAcctBalances
      .map((account) => {
        if (account.accountBalances.length === 0) {
          return {
            colorIndex: account.colorIndex,
            accountName: account.name,
            accountId: account.id,
            oneDayBalance: null,
            oneDayPercent: null,
            specialDayBalance: null,
            specialDayPercent: null,
            todayBalance: null,
          };
        }
        const { colorIndex } = account;
        const accountName = account.name;
        const today = account.accountBalances[account.accountBalances.length - 1];
        const yesterday = account.accountBalances[account.accountBalances.length - 2];
        const oneDayBalance = today && yesterday ? today.balanceAmount - yesterday.balanceAmount : null;
        const oneDayPercent =
          today && yesterday && yesterday?.balanceAmount !== 0
            ? ((today?.balanceAmount - yesterday?.balanceAmount) / yesterday?.balanceAmount) * 100
            : null;
        const specialStartDay = account.accountBalances.filter((day) => day.balanceOn === startDate);

        //  The following statement is so that if the first day of balances still fits within the daterange, the change in balance and percent still appear
        /*  if (!specialStartDay.length && startDate <= account.accountBalances[0].balanceOn && endDate > account.accountBalances[0].balanceOn) {
        specialStartDay.push(account.accountBalances[0]);
      } */
        const specialEndDay = account.accountBalances.filter((day) => day.balanceOn === endDate);

        //  The following statement is so that if the last day of balances still fits within the daterange, the change in balance and percent still appear
        /*  const lastBalanceIndex = account.accountBalances.length - 1;
      if (!specialEndDay.length && startDate < account.accountBalances[lastBalanceIndex].balanceOn && endDate >= account.accountBalances[lastBalanceIndex].balanceOn) {
        specialEndDay.push(account.accountBalances[lastBalanceIndex]);
      } */
        const specialDayBalance =
          specialStartDay.length && specialEndDay.length
            ? specialEndDay[0]?.balanceAmount - specialStartDay[0]?.balanceAmount
            : null;
        const specialDayPercent =
          specialStartDay.length && specialEndDay.length && specialStartDay[0].balanceAmount !== 0
            ? ((specialEndDay[0]?.balanceAmount - specialStartDay[0]?.balanceAmount) /
                specialStartDay[0]?.balanceAmount) *
              100
            : null;
        const todayBalance = today?.balanceAmount;

        return {
          colorIndex,
          accountId: account.id,
          accountName,
          oneDayBalance,
          oneDayPercent,
          specialDayBalance,
          specialDayPercent,
          todayBalance,
        };
      })
      .sort((unalteredValueA, unalteredValueB) => {
        if (orderBy === 'accountName') {
          const valueA = unalteredValueA[orderBy] ? String(unalteredValueA[orderBy]) : 'ZZZZZZZZZZZZZZ'; //  Undefined values go to the end of the list
          const valueB = unalteredValueB[orderBy] ? String(unalteredValueB[orderBy]) : 'ZZZZZZZZZZZZZZ';
          if (sortDirection === 'asc') {
            return valueA.localeCompare(valueB, undefined);
          }
          return valueB.localeCompare(valueA, undefined);
        }
        const valueA = unalteredValueA[orderBy] != null ? Number(unalteredValueA[orderBy]) : -99999999999999; //  Undefined values go to the end of the list
        const valueB = unalteredValueB[orderBy] != null ? Number(unalteredValueB[orderBy]) : -99999999999999;

        //  Using regular numeric comparisons because localeCompare numeric does NOT account for negative values!
        if (sortDirection === 'asc') {
          return valueB - valueA;
        }
        return valueA - valueB;
      });

    return ImmutableList(formattedBalances);
  },
);
